import { createFileRoute } from '@tanstack/react-router'
import { ComparisonPage, comparisonScripts, type ComparisonData } from '@/components/comparison-page'

export const Route = createFileRoute('/_site/compare/nixstats')({
  component: () => <ComparisonPage data={data} />,
  head: () => ({
    meta: [
      { title: 'NixStats Alternative — Uptrack Uptime Monitoring' },
      { name: 'description', content: "NixStats is moving to 360 Monitoring by Plesk. Compare Uptrack: 30-second checks from $3/mo, status pages, and alert confirmation." },
      { property: 'og:title', content: 'NixStats Alternative — Uptrack' },
      { property: 'og:description', content: "nixstats.com now redirects to 360 Monitoring. If you are reviewing options, compare Uptrack: 30s checks from $3/mo." },
      { property: 'og:image', content: 'https://uptrack.app/og/compare-nixstats.png' },
      { name: 'twitter:card', content: 'summary_large_image' },
    ],
    links: [
      { rel: 'canonical', href: 'https://uptrack.app/compare/nixstats' },
    ],
    scripts: comparisonScripts('nixstats', "NixStats"),
  }),
})

const data: ComparisonData = {
  competitor: "NixStats",
  tagline: "NixStats was a server and uptime monitoring service. Plesk is moving its users to 360 Monitoring, and nixstats.com now redirects there. If you are reviewing your options, here is how Uptrack compares.",
  heroDescription: "Plesk's migration FAQ describes the move from NixStats to 360 Monitoring; check it for current dates and plans. Uptrack facts come from its pricing page as of October 2026.",

  features: [
    { feature: "Product status", uptrack: "Active", competitor: "Users moving to 360 Monitoring", uptrackWins: true },
    { feature: "Server monitoring (agent)", uptrack: "Not available", competitor: "Core feature (CPU, RAM, disk)" },
    { feature: "Hosting panel integration", uptrack: "Not available", competitor: "360 Monitoring is part of the Plesk product family" },
  ],

  painPoints: [
    {
      title: "NixStats is moving to 360 Monitoring",
      description: "nixstats.com now redirects to 360 Monitoring, and Plesk's FAQ describes how NixStats accounts move over.",
    },
    {
      title: "A different product",
      description: "360 Monitoring is a separate product from Plesk/WebPros, with its own plans. A migration is a good time to compare your options.",
    },
    {
      title: "Server vs uptime focus",
      description: "NixStats centered on server metrics collected by an agent. Uptrack monitors from outside: HTTP, keyword, TCP, ping, DNS, SSL, heartbeat, and browser checks.",
    },
  ],

  differentiators: [
    "30-second checks on every plan: Pro is $3/mo for 50 monitors, Team is $20/mo for 200, Business is $79/mo for 1,000.",
    "Status pages on every plan: 1 on free, 5 on Pro, unlimited on Team and Business, with email subscriber notifications on paid plans.",
    "Alert confirmation: Uptrack opens an incident only after 2-5 consecutive failed checks, and several check locations must confirm. One DNS timeout or CDN blip does not page you.",
    "Browser checks: Pro includes 5 Playwright monitors that run flows like login or checkout and save a screenshot at each step.",
    "Flat, published pricing: $3, $20, or $79 per month. No per-seat or usage charges, and a 30-day money-back guarantee.",
    "REST API and an MCP server, so you can manage monitors from scripts or AI assistants such as Claude.",
  ],

  notForYou: [
    "If you need server monitoring (CPU, RAM, disk, processes), NixStats' successor 360 Monitoring or another server monitoring tool fits better.",
    "If you're already migrated to 360 Monitoring and it meets your needs.",
    "If you need a Plesk/hosting provider integration, 360 Monitoring is designed for that ecosystem.",
  ],
}
